/**
 * Query keys for the /api/v2 surface.
 *
 * Everything is prefixed `v2` and carries the org slug, so switching workspace
 * can never show another tenant's cached board, and `['v2']` invalidates the
 * whole Jira-like surface in one call. The foundation's `queryKeys` in
 * `@/lib/queryClient` covers the legacy /api endpoints and is left untouched.
 */

import type { IssueQuery } from './api'

export const issueKeys = {
  all: ['v2'] as const,
  session: ['v2', 'session'] as const,
  members: (slug: string) => ['v2', 'members', slug] as const,
  labels: (slug: string) => ['v2', 'labels', slug] as const,

  boards: (slug: string) => ['v2', 'board', slug] as const,
  board: (slug: string, projectKey: string, scope: string) =>
    ['v2', 'board', slug, projectKey, scope] as const,

  columns: (slug: string, projectKey: string) => ['v2', 'columns', slug, projectKey] as const,

  backlogs: (slug: string) => ['v2', 'backlog', slug] as const,
  backlog: (slug: string, projectKey: string, includeCompleted: boolean) =>
    ['v2', 'backlog', slug, projectKey, includeCompleted] as const,

  issueLists: (slug: string) => ['v2', 'issues', slug] as const,
  issueList: (slug: string, query: IssueQuery) => ['v2', 'issues', slug, query] as const,

  issue: (slug: string, ref: string) => ['v2', 'issue', slug, ref] as const,
  comments: (slug: string, ref: string) => ['v2', 'issue', slug, ref, 'comments'] as const,
  activity: (slug: string, ref: string) => ['v2', 'issue', slug, ref, 'activity'] as const,

  sprints: (slug: string, projectKey: string) => ['v2', 'sprints', slug, projectKey] as const,
}

/**
 * Every key that shows a list of issues. A create, delete, move or bulk edit
 * invalidates all of them — cheaper to reason about than tracking which board
 * scope a given issue belonged to.
 */
export function issueListKeys(slug: string): Array<readonly unknown[]> {
  return [issueKeys.boards(slug), issueKeys.backlogs(slug), issueKeys.issueLists(slug)]
}
